// Companies grid (kind='company'). Mirror of PeopleView with a
// company-shaped column set.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Contact } from '@personical/cc-shared-types';
import { useApi } from '../../components/api-context.js';
import { useAuth } from '../../auth.js';
import { contactsToCsv, downloadFile } from '../../csv.js';
import { FAB } from '../../app/FAB.js';
import { BulkAction, BulkActionBar } from '../../grid/BulkActionBar.js';
import { DataGrid } from '../../grid/DataGrid.js';
import { COMPANY_COLUMNS } from '../../grid/columns/contactColumns.js';
import { SavedViewsBar } from '../../grid/SavedViews.js';
import { useDensity } from '../../grid/useDensity.js';
import { sortContacts, useGridFilters } from '../../grid/useFilters.js';
import { useRowMutations } from '../../grid/useRowMutations.js';
import { ContactPanel } from '../../panels/ContactPanel.js';
import { useToast } from '../../toast.js';
import { GridToolbar } from '../GridToolbar.js';

export function CompaniesView() {
  const api = useApi();
  const navigate = useNavigate();
  const params = useParams();
  const detailId = params.id ?? null;
  const { state } = useAuth();
  const currentOid = state.kind === 'authenticated' ? state.user.oid : null;
  const tenantId = state.kind === 'authenticated' ? state.user.tenantId : '';

  const toast = useToast();
  const [density] = useDensity();
  const { filters, setQuery, setSort } = useGridFilters();
  const [rows, setRows] = useState<Contact[] | null>(null);
  const [continuation, setContinuation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const mutations = useRowMutations({
    api,
    rows,
    setRows,
    onError: (err) => toast.error(err.message)
  });

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setContinuation(null);
    setError(null);
    setSelected(new Set());
    api
      .list({ kind: 'company', query: filters.query || undefined })
      .then((res) => {
        if (cancelled) return;
        setRows(res.items);
        setContinuation(res.continuationToken);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [api, filters.query]);

  async function loadMore() {
    if (!continuation || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.list({
        kind: 'company',
        query: filters.query || undefined,
        continuationToken: continuation
      });
      setRows((prev) => [...(prev ?? []), ...res.items]);
      setContinuation(res.continuationToken);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }

  function exportSelected() {
    if (!rows) return;
    const picked = rows.filter((c) => selected.has(c.id));
    if (picked.length === 0) return;
    downloadFile(`companies-${new Date().toISOString().slice(0, 10)}.csv`, contactsToCsv(picked));
    toast.success(`Exported ${picked.length} ${picked.length === 1 ? 'row' : 'rows'} to CSV.`);
  }

  async function trashSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!confirm(`Move ${ids.length} ${ids.length === 1 ? 'company' : 'companies'} to trash?`)) return;
    setBulkBusy(true);
    try {
      await Promise.all(ids.map((id) => api.remove(id)));
      setRows((prev) => prev?.filter((c) => !selected.has(c.id)) ?? prev);
      setSelected(new Set());
      toast.success(`Moved ${ids.length} to trash.`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBulkBusy(false);
    }
  }

  const filtered = rows
    ? rows.filter((c) => {
        if (filters.tag && !c.tags.includes(filters.tag)) return false;
        if (filters.favOnly && currentOid && !(c.favoritedBy ?? []).includes(currentOid)) return false;
        return true;
      })
    : [];
  const sorted = sortContacts(filtered, filters.sort);

  return (
    <div className="space-y-4">
      <GridToolbar
        title="Companies"
        count={rows?.length ?? null}
        query={filters.query}
        onQueryChange={setQuery}
        newHref="/contacts/new?kind=company"
      />
      <div className="hidden md:block">
        <SavedViewsBar scope={`${tenantId}:companies`} />
      </div>
      {error && (
        <p className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {error}
        </p>
      )}
      <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())} busy={bulkBusy}>
        <BulkAction onClick={exportSelected} disabled={bulkBusy}>
          Export CSV
        </BulkAction>
        <BulkAction onClick={trashSelected} disabled={bulkBusy} variant="danger">
          Move to trash
        </BulkAction>
      </BulkActionBar>
      <DataGrid
        columns={COMPANY_COLUMNS}
        rows={sorted}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/companies/${r.id}` + window.location.search)}
        sort={filters.sort}
        onSortChange={setSort}
        context={{ currentOid, ...mutations }}
        selection={{ selected, onChange: setSelected }}
        density={density}
        loading={rows === null}
        empty="No companies yet."
      />
      {detailId && (
        <ContactPanel
          id={detailId}
          open
          onClose={() => navigate(`/companies` + window.location.search)}
          onChange={(updated) => {
            if (updated === null) {
              setRows((prev) => prev?.filter((c) => c.id !== detailId) ?? prev);
            } else {
              setRows((prev) =>
                prev?.map((c) => (c.id === updated.id ? updated : c)) ?? prev
              );
            }
          }}
        />
      )}
      {continuation && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="btn-secondary px-4 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
      <FAB href="/contacts/new?kind=company" label="New company" />
    </div>
  );
}
